import { attachmentFileId, attachmentStorageKey, bucketProblem, isSensitiveAttachmentKind, type AttachmentRegisterV1, type FileBucket } from '@gk/contracts'
import type { ClubApi } from '../club/ClubProvider.tsx'
import type { BlobRow, GkDb } from '../sync/db.ts'
import type { EnqueueOptions } from '../sync/outbox.ts'
import type { PreparedDocument, PreparedPhoto } from './capture.ts'

/** Pictures are only worth taking while the phone can keep them: the device refuses NEW photos at 90 % of its storage quota, before it ever has to refuse a command
 *  (ARCHITECTURE 9.10). A real QuotaExceededError from the database says the same thing. */
export class PhotoQuotaError extends Error { constructor() { super('PHOTO_QUOTA') } }
export const QUOTA_LIMIT = 0.9

export async function captureAllowed(): Promise<boolean> {
  try {
    const e = await navigator.storage?.estimate?.()
    return !(e?.quota && e.usage !== undefined && e.usage / e.quota >= QUOTA_LIMIT)
  } catch { return true }
}
const isQuota = (e: unknown): boolean => {
  const x = e as { name?: string; inner?: unknown } | null
  return !!x && (x.name === 'QuotaExceededError' || (!!x.inner && isQuota(x.inner)))
}

export interface PhotoLink { entity_type: AttachmentRegisterV1['entity_type']; entity_id: string; purpose?: string; caption?: string | null }
type Send = ClubApi['send']

/** Keep the picture and tell the server about it IN ONE LOCAL TRANSACTION: the outbox's register command and the bytes it announces appear together or not at all. */
export async function addPhoto(db: GkDb, send: Send, orgId: string, siteId: string, photo: PreparedPhoto, link: PhotoLink, opts: { dependsOn?: string[] } = {}): Promise<{ commandId: string }> {
  if (!(await captureAllowed())) throw new PhotoQuotaError()
  const fileId = attachmentFileId(orgId, photo.sha256)
  try {
    return await send('attachment.register@1', {
      sha256: photo.sha256, bytes: photo.bytes, mime: photo.mime, captured_at: photo.capturedAt, entity_type: link.entity_type, entity_id: link.entity_id,
      ...(link.purpose ? { purpose: link.purpose } : {}), ...(link.caption ? { caption: link.caption } : {}),
    }, {
      ...(opts.dependsOn?.length ? { dependsOn: opts.dependsOn } : {}),
      alsoWrite: async (row) => {
        const sensitive = isSensitiveAttachmentKind(link.entity_type)
        const existing = await db.blobs.get(photo.sha256)
        if (existing) {
          await db.blobs.update(photo.sha256, { register_command_ids: [...existing.register_command_ids, row.command_id], ...(existing.state === 'failed' ? { state: 'registered' as const, attempts: 0 } : {}), sensitive: existing.sensitive && sensitive ? true : undefined })
          return
        }
        const blob: BlobRow = {
          sha256: photo.sha256, bytes: photo.bytes, mime: photo.mime, blob: photo.blob, file_id: fileId, storage_key: attachmentStorageKey(orgId, siteId, photo.sha256, photo.mime),
          register_command_ids: [row.command_id], state: 'registered', attempts: 0, created_at: new Date().toISOString(), ...(sensitive ? { sensitive: true as const } : {}),
        }
        await db.blobs.add(blob)
      },
    })
  } catch (e) { if (isQuota(e)) throw new PhotoQuotaError(); throw e }
}

// ---------------------------------------------------------------------------------------------------------- M15-V: documents
/** The bytes of a document kept on the device for the lane, in the transaction of the command that announces them (the register, or the vault's publish):
 *  a file this device already holds (a retry, the same PDF for two versions) only gains the command. The row says its bucket (the lane uploads there). */
function keepBytes(db: GkDb, orgId: string, siteId: string, doc: PreparedDocument, bucket: FileBucket, sensitive = false): NonNullable<EnqueueOptions['alsoWrite']> {
  return async (row) => {
    const existing = await db.blobs.get(doc.sha256)
    if (existing) {
      await db.blobs.update(doc.sha256, { register_command_ids: [...existing.register_command_ids, row.command_id], ...(existing.state === 'failed' ? { state: 'registered' as const, attempts: 0 } : {}), sensitive: existing.sensitive && sensitive ? true : undefined })
      return
    }
    const blob: BlobRow = {
      sha256: doc.sha256, bytes: doc.bytes, mime: doc.mime, blob: doc.blob, file_id: attachmentFileId(orgId, doc.sha256), storage_key: attachmentStorageKey(orgId, siteId, doc.sha256, doc.mime),
      register_command_ids: [row.command_id], state: 'registered', attempts: 0, created_at: new Date().toISOString(), ...(bucket === 'documents' ? { bucket } : {}), ...(sensitive ? { sensitive: true as const } : {}),
    }
    await db.blobs.add(blob)
  }
}

/** The bucket refused the file before anything was sent (the type, the size, the purpose: what the server would refuse first). */
export class DocumentRefusedError extends Error {
  readonly code: string
  constructor(code: string) { super(code); this.code = code }
}

/** A document on a record (an invoice's PDF on a machine's cost, M15-E1; any PDF a record may carry): `attachment.register@2` with its bucket and the bytes
 *  in ONE local transaction, as `addPhoto` does for a photo. The documents bucket unless another is named; the bucket's rules are said here first. */
export async function addDocument(db: GkDb, send: Send, orgId: string, siteId: string, doc: PreparedDocument, link: PhotoLink & { bucket?: FileBucket }, opts: { dependsOn?: string[] } = {}): Promise<{ commandId: string }> {
  const bucket = link.bucket ?? 'documents'
  const refused = bucketProblem(bucket, doc.mime, doc.bytes, link.purpose)
  if (refused) throw new DocumentRefusedError(refused)
  if (!(await captureAllowed())) throw new PhotoQuotaError()
  try {
    return await send('attachment.register@2', {
      sha256: doc.sha256, bytes: doc.bytes, mime: doc.mime, captured_at: doc.capturedAt, entity_type: link.entity_type, entity_id: link.entity_id, bucket,
      ...(link.purpose ? { purpose: link.purpose } : {}), ...(link.caption ? { caption: link.caption } : {}),
    }, { ...(opts.dependsOn?.length ? { dependsOn: opts.dependsOn } : {}), alsoWrite: keepBytes(db, orgId, siteId, doc, bucket, isSensitiveAttachmentKind(link.entity_type)) })
  } catch (e) { if (isQuota(e)) throw new PhotoQuotaError(); throw e }
}

/** The vault's publish (`document.publish_version@2`, an intent: sendAndWait) keeps the file's bytes in its own local transaction (`alsoWrite`), as
 *  `illustrateOptions` does for a procedure's pictures: the lane uploads them once the publish is applied and confirms them. Refuses before anything is
 *  written when the device is short of storage (M8). */
export async function documentPublishOptions(db: GkDb, orgId: string, siteId: string, doc: PreparedDocument): Promise<EnqueueOptions> {
  if (!(await captureAllowed())) throw new PhotoQuotaError()
  return { alsoWrite: keepBytes(db, orgId, siteId, doc, 'documents') }
}

/** A publish the server refused brought bytes nobody needs: they go once EVERY command that names them is settled without being applied (refused, in
 *  conflict, discarded); one applied or still on its way keeps them for the lane. */
const SETTLED = new Set(['rejected', 'conflict', 'error', 'discarded'])
export async function forgetRefusedBytes(db: GkDb, sha256: string): Promise<boolean> {
  return db.transaction('rw', [db.blobs, db.outbox, db.archive], async () => {
    const row = await db.blobs.get(sha256)
    if (!row || row.state === 'uploaded') return false
    for (const id of row.register_command_ids) {
      if (await db.archive.get(id)) return false
      const out = await db.outbox.get(id)
      if (out && !SETTLED.has(out.status)) return false
    }
    await db.blobs.delete(sha256)
    return true
  })
}
